import { useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';

/**
 * 화면 찾기(게시판 #396 정여진 — 「출납」「입금」을 치면 그 화면으로).
 * 메뉴 + 세부 탭(회계 › 출납일보 등)을 이름으로 찾는다. 목록은 껍데기(App.tsx)가 **권한으로 거른 메뉴**에서 만들어 넘긴다 —
 * 여기서 따로 고르지 않는다(못 여는 화면이 결과에 섞이지 않게).
 */
export type MenuSearchItem = { label: string; to: string };

/** 띄어쓰기·구분 기호를 빼고 비교 — 「출납 일보」「회계출납」도 걸린다. */
const norm = (s: string) => s.replace(/[\s·›>/()]/g, '').toLowerCase();

export function matchMenu(items: MenuSearchItem[], q: string): MenuSearchItem[] {
  const k = norm(q);
  if (!k) return [];
  const hit = items.filter((it) => norm(it.label).includes(k));
  // 마지막 이름(세부 탭 이름)이 친 글자로 시작하는 것 먼저 — 「입금」이면 「회계 › 입금 내역」이 위로.
  const last = (it: MenuSearchItem) => norm(it.label.split('›').pop() ?? it.label);
  return hit.sort((a, b) => Number(last(b).startsWith(k)) - Number(last(a).startsWith(k)));
}

export default function MenuSearch({ items, onGo, className = '' }: { items: MenuSearchItem[]; onGo?: () => void; className?: string }) {
  const navigate = useNavigate();
  const [q, setQ] = useState('');
  const [open, setOpen] = useState(false);
  const [sel, setSel] = useState(0);
  const inputRef = useRef<HTMLInputElement>(null);
  const hits = useMemo(() => matchMenu(items, q).slice(0, 12), [items, q]);

  function go(it: MenuSearchItem | undefined) {
    if (!it) return;
    navigate(it.to);
    setQ('');
    setOpen(false);
    inputRef.current?.blur();
    onGo?.();
  }

  return (
    <div className={`relative ${className}`}>
      <input
        ref={inputRef}
        value={q}
        onChange={(e) => { setQ(e.target.value); setSel(0); setOpen(true); }}
        onFocus={() => setOpen(true)}
        // 결과를 누르는 동안 blur 가 먼저 와 목록이 닫히지 않게 조금 늦춘다.
        onBlur={() => window.setTimeout(() => setOpen(false), 150)}
        onKeyDown={(e) => {
          if (e.key === 'ArrowDown') { e.preventDefault(); setSel((s) => Math.min(s + 1, hits.length - 1)); }
          else if (e.key === 'ArrowUp') { e.preventDefault(); setSel((s) => Math.max(s - 1, 0)); }
          else if (e.key === 'Enter') { e.preventDefault(); go(hits[sel]); }
          else if (e.key === 'Escape') { setQ(''); setOpen(false); }
        }}
        placeholder="🔍 화면 찾기 (예: 출납, 입금)"
        aria-label="화면 찾기"
        // #442 「검색이 눈에 잘 안 띈다」 — 없는 클래스(input)라 테두리 없이 바탕에 묻혀 있었다 → 파란 테두리·흰 바탕.
        className="w-full rounded-lg border-2 border-brand-500 bg-surface px-2.5 py-1 text-sm font-medium text-ink shadow-sm outline-none placeholder:text-ink-soft focus:ring-2 focus:ring-brand-100"
      />
      {open && q.trim() !== '' && (
        <ul className="absolute right-0 z-50 mt-1 max-h-80 w-full min-w-[14rem] overflow-y-auto rounded-lg border border-line bg-surface py-1 shadow-lg">
          {hits.length === 0 ? (
            <li className="px-3 py-2 text-sm text-ink-faint">찾는 화면이 없습니다</li>
          ) : (
            hits.map((it, i) => (
              <li key={it.to}>
                <button
                  type="button"
                  onMouseDown={(e) => e.preventDefault()}
                  onMouseEnter={() => setSel(i)}
                  onClick={() => go(it)}
                  className={`block w-full whitespace-nowrap px-3 py-1.5 text-left text-sm ${i === sel ? 'bg-brand-50 text-brand-700' : 'text-ink'}`}
                >
                  {it.label}
                </button>
              </li>
            ))
          )}
        </ul>
      )}
    </div>
  );
}
